import { Check, ArrowRight, X } from "lucide-react";

export interface ChatReadyToastProps {
    title: string;
    actionHref?: string;
    onAction: () => void;
    onDismiss: () => void;
}

const FALLBACK_TITLES = new Set(["new chat"]);

function isFallbackTitle(title: string): boolean {
    return !title || FALLBACK_TITLES.has(title.trim().toLowerCase());
}

export function ChatReadyToast({
    title,
    actionHref,
    onAction,
    onDismiss,
}: ChatReadyToastProps) {
    const hasTitle = !isFallbackTitle(title);

    return (
        <div
            role="status"
            aria-live="polite"
            className="border-border bg-card relative flex w-full max-w-[356px] items-start gap-3 rounded-xl border p-4 shadow-lg dark:shadow-[0_4px_24px_rgba(0,0,0,0.4)]"
        >
            <div className="text-primary mt-0.5 flex size-5 shrink-0 items-center justify-center">
                <Check className="size-4" strokeWidth={2.5} />
            </div>

            <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                {hasTitle ? (
                    <>
                        <span className="text-muted-foreground text-xs font-medium tracking-wide">
                            Chat ready
                        </span>
                        <span className="text-card-foreground truncate text-sm font-semibold">
                            {title}
                        </span>
                    </>
                ) : (
                    <span className="text-card-foreground text-sm font-semibold">
                        Your chat is ready
                    </span>
                )}

                <a
                    href={actionHref ?? "#"}
                    onClick={(e) => {
                        e.preventDefault();
                        onAction();
                    }}
                    className="text-primary focus-visible:ring-ring mt-1 inline-flex w-fit items-center gap-1 text-sm font-medium hover:underline focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                >
                    View chat
                    <ArrowRight className="size-3.5" />
                </a>
            </div>

            <button
                type="button"
                onClick={onDismiss}
                aria-label="Dismiss notification"
                className="text-muted-foreground hover:bg-muted hover:text-card-foreground focus-visible:ring-ring absolute top-2 right-2 flex size-6 items-center justify-center rounded-md opacity-60 transition-opacity before:absolute before:-inset-2 before:content-[''] hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none"
            >
                <X className="size-3.5" />
            </button>
        </div>
    );
}
